import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from './ThemeContext';
import { useNavigate } from 'react-router-dom';
import { searchColleges, getCarouselItems } from '../../services/api';
import { getTopUniversities } from '../../services/collegeloadapi';
import { getPublicQuickLinks } from '../../services/filter';
import {
  Search, Globe, Award, Star, Target, School, MapPin, Cpu, Briefcase, HeartPulse, Atom,
  BookOpen, X, Loader2, ClipboardCheck, Brain, Trophy, FileCheck, Clock, DollarSign, Calendar,
  GraduationCap, TrendingUp, Zap, ImageOff, ArrowRight, ChevronRight
} from 'lucide-react';
import { motion, AnimatePresence } from "framer-motion";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// ─── UTILITY ──────────────────────────────────────────────────────────────────
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001/api';
  const host = backendUrl.replace('/api', '');
  const clean = imagePath.startsWith('uploads/') ? imagePath.replace('uploads/', '') : imagePath;
  return `${host}/uploads/${clean}`;
};

// ─── COMPONENTS ───────────────────────────────────────────────────────────────
const DefaultLogoIcon = ({ collegeName }) => (
  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-100 to-indigo-200 flex items-center justify-center flex-shrink-0">
    <span className="text-sm font-bold text-indigo-600">{collegeName ? collegeName.charAt(0) : '?'}</span>
  </div>
);

const CollegeLogo = ({ src, alt, collegeName }) => {
  const [imgError, setImgError] = useState(false);
  const [loading, setLoading] = useState(true);
  const imageUrl = getImageUrl(src);

  useEffect(() => { setImgError(false); setLoading(!!imageUrl); }, [imageUrl]);

  if (imgError || !imageUrl) return <DefaultLogoIcon collegeName={collegeName} />;

  return (
    <div className="relative w-10 h-10 flex-shrink-0">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-xl">
          <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />
        </div>
      )}
      <img
        src={imageUrl} alt={alt}
        className={`w-full h-full rounded-xl object-contain ${loading ? 'opacity-0' : 'opacity-100'} transition-opacity`}
        onLoad={() => setLoading(false)}
        onError={() => { setImgError(true); setLoading(false); }}
      />
    </div>
  );
};

const RankChip = ({ rank, label, rankType, isDarkMode }) => {
  const rankValue = String(rank || '').trim();
  if (!rankValue || rankValue === '0' || rankValue === 'null') return null;
  const styles = {
    world: isDarkMode ? 'bg-indigo-900/30 text-indigo-400' : 'bg-indigo-100 text-indigo-700',
    nirf: isDarkMode ? 'bg-teal-900/30 text-teal-400' : 'bg-teal-100 text-teal-700',
    state: isDarkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700',
    general: isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700',
  };
  return (
    <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-bold whitespace-nowrap ${styles[rankType] || styles.general}`}>
      {label}: {rankValue}
    </span>
  );
};

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const countries = ['India', 'USA', 'UK', 'Canada', 'Australia', 'Germany', 'France', 'Netherlands', 'Singapore', 'New Zealand', 'Ireland', 'Other'];

const DEFAULT_CAROUSEL_ITEMS = [
  { number: '1', title: 'Explore', description: "Browse thousands of global Master's programmes.", color1: '#3B82F6', color2: '#8B5CF6', imageUrl: '', mediaType: 'image' },
  { number: '2', title: 'Decide', description: 'Shortlist your top choices and compare options.', color1: '#8B5CF6', color2: '#EC4899', imageUrl: '', mediaType: 'image' },
  { number: '3', title: 'Apply', description: 'Confidently submit your dream university application.', color1: '#EC4899', color2: '#F59E0B', imageUrl: '', mediaType: 'image' }
];

const servicesOffered = [
  { icon: <ClipboardCheck className="w-8 h-8 lg:w-12 lg:h-12 text-blue-500" />, title: 'Admissions', items: ['Regular Admission India & Abroad', 'Distance/Correspondence Learning', 'Online Certification'], target: '/student/colleges' },
  { icon: <Brain className="w-8 h-8 lg:w-12 lg:h-12 text-purple-500" />, title: 'Counselling', items: ['Career Counselling', 'Mental Health Counselling', 'Stream/Subject Counselling'], target: '/services/counselling' },
  { icon: <Trophy className="w-8 h-8 lg:w-12 lg:h-12 text-pink-500" />, title: 'Coaching & Preparation', items: ['IIT-JEE, NEET, NDA', 'CAT, CUET, CLAT', 'GMAT, TOEFL, SAT'], target: '/services/coaching' },
  { icon: <FileCheck className="w-8 h-8 lg:w-12 lg:h-12 text-green-500" />, title: 'Training & Certification', items: ['NEP-Based Courses', 'Industry Certifications', 'Professional Skills'], target: '/training' }
];

const popularCourses = [
  { name: 'Computer Science', icon: <Cpu className="w-6 h-6 lg:w-8 lg:h-8 text-blue-500" />, query: 'Computer Science' },
  { name: 'Business Management', icon: <Briefcase className="w-6 h-6 lg:w-8 lg:h-8 text-orange-500" />, query: 'Business Management' },
  { name: 'Medicine & Health', icon: <HeartPulse className="w-6 h-6 lg:w-8 lg:h-8 text-red-500" />, query: 'Medicine' },
  { name: 'Quantum Physics', icon: <Atom className="w-6 h-6 lg:w-8 lg:h-8 text-purple-400" />, query: 'Physics' }
];

// ─── Description Modal ────────────────────────────────────────────────────────
const DescriptionModal = ({ title, description, imageUrl, isDarkMode, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
      >
        <motion.div
          className={`w-full max-w-lg rounded-2xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'}`}
          initial={{ y: 50, scale: 0.9 }} animate={{ y: 0, scale: 1 }} exit={{ y: 50, scale: 0.9 }}
          onClick={e => e.stopPropagation()}
        >
          <div className="flex justify-between items-start border-b pb-3 mb-4">
            <h3 className="text-2xl font-bold">{title}</h3>
            <button onClick={onClose} className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}><X className="w-6 h-6" /></button>
          </div>
          {imageUrl && <div className="mb-4 rounded-xl overflow-hidden"><img src={getImageUrl(imageUrl)} alt={title} className="w-full h-48 object-cover" /></div>}
          <p className={`text-base whitespace-pre-wrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{description}</p>
          <div className="mt-6 text-right">
            <button onClick={onClose} className="px-5 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">Close</button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ─── Universities Ticker ──────────────────────────────────────────────────────
const UniversitiesTicker = ({ isDarkMode, universities, onCollegeClick }) => {
  const [isPaused, setIsPaused] = useState(false);
  if (!universities || universities.length === 0) return (
    <div className={`p-10 text-center rounded-xl ${isDarkMode ? 'bg-gray-800/50 text-gray-400' : 'bg-gray-100/80 text-gray-600'}`}>
      <ImageOff className="w-12 h-12 mx-auto mb-4" />
      <p>No featured universities available right now.</p>
    </div>
  );
  return (
    <div className="relative w-full overflow-hidden py-10 -my-4" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
      <div
        className={`flex items-center gap-6 ${isPaused ? '' : 'animate-ticker'}`}
        style={{ width: 'max-content', animationPlayState: isPaused ? 'paused' : 'running' }}
      >
        {[...universities, ...universities].map((uni, index) => (
          <div
            key={`${uni._id}-${index}`}
            onClick={() => onCollegeClick(uni)}
            className={`inline-block flex-shrink-0 w-80 md:w-96 cursor-pointer group relative backdrop-blur-xl rounded-2xl lg:rounded-3xl p-4 sm:p-5 lg:p-6 border shadow-xl hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 ${
              isDarkMode
                ? 'bg-gray-800/50 border-gray-700/50 hover:border-indigo-500/50 hover:shadow-indigo-500/20'
                : 'bg-white/80 border-gray-200 hover:border-indigo-500/50 hover:shadow-indigo-500/10'
            }`}
          >
            <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className={`p-2 sm:p-3 rounded-xl ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'} group-hover:scale-110 transition-transform flex-shrink-0`}>
                {uni.iconUrl
                  ? <img src={getImageUrl(uni.iconUrl)} alt={uni.name} className="w-8 h-8 lg:w-10 lg:h-10 object-contain" />
                  : <School className="w-8 h-8 lg:w-10 lg:h-10 text-indigo-500" />}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`text-base lg:text-xl font-bold truncate group-hover:text-indigo-600 transition-colors ${isDarkMode ? 'text-white' : 'text-black'}`}>{uni.name}</h3>
                <div className={`flex items-center gap-1 text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <MapPin className="w-3 h-3 sm:w-4 sm:h-4" /><span>{uni.location || 'Location N/A'}</span>
                </div>
              </div>
              <ChevronRight className={`w-4 h-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              <RankChip rank={uni.worldRank} label="W.Rank" rankType="world" isDarkMode={isDarkMode} />
              <RankChip rank={uni.nirfRank} label="NIRF" rankType="nirf" isDarkMode={isDarkMode} />
              <RankChip rank={uni.stateRank} label="S.Rank" rankType="state" isDarkMode={isDarkMode} />
              {uni.rank && !uni.worldRank && !uni.nirfRank && !uni.stateRank && <RankChip rank={uni.rank} label="Rank" rankType="general" isDarkMode={isDarkMode} />}
            </div>
          </div>
        ))}
      </div>
      <style jsx>{`
        @keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-ticker { animation: ticker 50s linear infinite; }
      `}</style>
    </div>
  );
};

// ─── Animated Tagline ─────────────────────────────────────────────────────────
function AnimatedTagline({ isDarkMode, items }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [modalData, setModalData] = useState(null);

  useEffect(() => {
    if (items.length === 0) return;
    const t = setInterval(() => setCurrentStep(p => (p + 1) % items.length), 4000);
    return () => clearInterval(t);
  }, [items.length]);

  const item = items[currentStep] || {};
  const isLong = item.description && item.description.length > 120;
  const truncated = isLong ? item.description.substring(0, 120) + '...' : item.description;

  return (
    <>
      <div className="relative w-full max-w-lg mx-auto" style={{ minHeight: '400px' }}>
        {items.map((it, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.8, rotateY: -90 }}
            animate={{ opacity: currentStep === i ? 1 : 0, scale: currentStep === i ? 1 : 0.8, rotateY: currentStep === i ? 0 : 90, zIndex: currentStep === i ? 10 : 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="absolute inset-0 flex flex-col items-center justify-center p-6 sm:p-8"
          >
            <motion.div
              animate={{ scale: currentStep === i ? [1, 1.1, 1] : 1, rotate: currentStep === i ? [0, 5, -5, 0] : 0 }}
              transition={{ duration: 2, repeat: currentStep === i ? Infinity : 0, ease: "easeInOut" }}
              className="relative mb-8"
            >
              {it.imageUrl && it.mediaType === 'image' ? (
                <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full flex items-center justify-center overflow-hidden shadow-2xl"
                  style={{ background: `linear-gradient(135deg, ${it.color1}, ${it.color2})`, boxShadow: `0 20px 60px ${it.color1}60, 0 0 0 20px ${it.color1}20` }}>
                  <img src={getImageUrl(it.imageUrl)} alt={it.title} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full flex items-center justify-center text-6xl sm:text-7xl font-black text-white shadow-2xl"
                  style={{ background: `linear-gradient(135deg, ${it.color1}, ${it.color2})`, boxShadow: `0 20px 60px ${it.color1}60, 0 0 0 20px ${it.color1}20` }}>
                  {it.number}
                </div>
              )}
            </motion.div>
            <motion.h3 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4 text-center"
              style={{ background: `linear-gradient(135deg, ${it.color1}, ${it.color2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {it.title}
            </motion.h3>
            <motion.p className={`text-lg sm:text-xl text-center max-w-md font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {currentStep === i && (<>{truncated} {isLong && <button onClick={() => setModalData(it)} className="font-semibold ml-2 bg-blue-600 text-white py-1 px-3 rounded-md hover:bg-blue-700 text-sm">Read More</button>}</>)}
            </motion.p>
          </motion.div>
        ))}
      </div>
      {modalData && <DescriptionModal title={modalData.title} description={modalData.description} imageUrl={modalData.imageUrl} isDarkMode={isDarkMode} onClose={() => setModalData(null)} />}
    </>
  );
}

// ─── Floating Icons ───────────────────────────────────────────────────────────
function FloatingIcons() {
  const icons = [
    { Icon: GraduationCap, color: 'text-blue-500', delay: 0 },
    { Icon: TrendingUp, color: 'text-purple-500', delay: 0.5 },
    { Icon: Award, color: 'text-pink-500', delay: 1 },
    { Icon: Globe, color: 'text-green-500', delay: 1.5 },
    { Icon: Zap, color: 'text-yellow-500', delay: 2 }
  ];
  return (
    <>
      {icons.map(({ Icon, color, delay }, i) => (
        <motion.div key={i} className={`absolute ${color}`}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: [0, 1, 1, 0], scale: [0, 1.2, 1, 0], x: [0, Math.random() * 100 - 50, Math.random() * 150 - 75], y: [0, -100, -200] }}
          transition={{ duration: 3, repeat: Infinity, delay, ease: "easeInOut" }}
          style={{ left: `${20 + i * 15}%`, top: '50%' }}>
          <Icon className="w-8 h-8 sm:w-12 sm:h-12" />
        </motion.div>
      ))}
    </>
  );
}

// ─── SEARCH SUGGESTION ITEM ────────────────────────────────────────────────────
const SuggestionItem = ({ college, onDirectVisit, onSearchList, isDarkMode }) => (
  <div className={`flex items-center gap-3 p-3 border-b last:border-b-0 transition-colors ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
    <button
      onClick={() => onSearchList(college)}
      className={`flex items-center gap-3 flex-1 min-w-0 text-left rounded-lg p-1 transition-colors ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
    >
      <CollegeLogo src={college.collegeLogo || college.imageUrl} alt={college.collegeName} collegeName={college.collegeName} />
      <div className="flex-1 min-w-0">
        <div className={`font-semibold truncate text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{college.collegeName}</div>
        {(college.city || college.state) && (
          <div className={`text-xs flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <MapPin className="w-3 h-3" />
            {college.city}{college.city && college.state ? ', ' : ''}{college.state}
          </div>
        )}
        {college.courses?.length > 0 && (
          <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
            {college.courses.length} program{college.courses.length > 1 ? 's' : ''} available
          </div>
        )}
      </div>
    </button>
    <button
      onClick={() => onDirectVisit(college)}
      className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 active:scale-95 transition-all shadow-sm"
    >
      Visit <ChevronRight className="w-3 h-3" />
    </button>
  </div>
);

// ─── MAIN HERO ────────────────────────────────────────────────────────────────
export default function Hero() {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const [liveTime, setLiveTime] = useState(new Date());
  const [selectedCountry, setSelectedCountry] = useState('');
  const [customCountry, setCustomCountry] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeoutRef = useRef(null);
  const suggestionsRef = useRef(null);

  const [liveCarouselItems, setLiveCarouselItems] = useState(DEFAULT_CAROUSEL_ITEMS);
  const [carouselLoading, setCarouselLoading] = useState(true);
  const [topUniversities, setTopUniversities] = useState([]);
  const [universitiesLoading, setUniversitiesLoading] = useState(true);
  const [quickLinks, setQuickLinks] = useState([]);

  // Live clock
  useEffect(() => {
    const t = setInterval(() => setLiveTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // API data fetch
  useEffect(() => {
    (async () => {
      try {
        const [carouselRes, uniRes, linksRes] = await Promise.allSettled([
          getCarouselItems(),
          getTopUniversities(),
          getPublicQuickLinks()
        ]);
        if (carouselRes.status === 'fulfilled' && carouselRes.value.data?.data?.length > 0) {
          setLiveCarouselItems(carouselRes.value.data.data.sort((a, b) => a.order - b.order));
        }
        if (uniRes.status === 'fulfilled' && uniRes.value.data) setTopUniversities(uniRes.value.data);
        if (linksRes.status === "fulfilled" && Array.isArray(linksRes.value) && linksRes.value.length > 0) setQuickLinks(linksRes.value);
      } catch (err) { console.error("API Fetch Error:", err); }
      finally { setCarouselLoading(false); setUniversitiesLoading(false); }
    })();
  }, []);

  // Click outside to close suggestions
  useEffect(() => {
    const handler = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ✅ HELPER: build clean URL params for navigation
  const buildSearchParams = (query, country) => {
    const params = new URLSearchParams();
    const q = (query || '').trim();
    const c = country === 'Other' ? customCountry : country;
    if (q) params.set('q', q);
    if (c) params.set('country', c);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  };

  // Search input handler
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!value.trim()) { setShowSuggestions(false); setSearchResults([]); return; }
    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const country = selectedCountry === 'Other' ? customCountry : selectedCountry;
        const params = { q: value };
        if (country) params.country = country;
        const res = await searchColleges(params);
        const results = res.data?.data || res.data || [];
        setSearchResults(results);
        setShowSuggestions(results.length > 0);
      } catch { setSearchResults([]); }
      finally { setIsSearching(false); }
    }, 300);
  };

  // ── NAVIGATION HANDLERS ────────────────────────────────────────────────────

  // ✅ FIX: Form submit → navigate to colleges tab with ?q= in URL
  const handleSearch = (e, overrideQuery) => {
    if (e) e.preventDefault();
    const query = (overrideQuery || searchQuery).trim();
    const qs = buildSearchParams(query, selectedCountry);
    setShowSuggestions(false);
    navigate(`/student/colleges${qs}`);
  };

  // ✅ FIX: Suggestion click → colleges list filtered by this college name
  const handleSuggestionSearchList = (college) => {
    const qs = buildSearchParams(college.collegeName, selectedCountry);
    setSearchQuery(college.collegeName);
    setShowSuggestions(false);
    navigate(`/student/colleges${qs}`);
  };

  // "Visit" button → direct college detail page
  const handleSuggestionDirectVisit = (college) => {
    setShowSuggestions(false);
    const id = college._id || college.id;
    if (id) {
      navigate(`/college/${id}`, { state: { from: '/' } });
    } else {
      const qs = buildSearchParams(college.collegeName, selectedCountry);
      navigate(`/student/colleges${qs}`);
    }
  };

  // Ticker card click → direct to college detail
  const handleTickerCollegeClick = (uni) => {
    const id =
        uni.collegeId ||
        uni.college?._id ||
        uni.id ||
        uni._id;

    if (id) {
        navigate(`/college/${id}`, { state: { from: '/' } });
    } else {
        navigate(`/student/colleges?q=${encodeURIComponent(uni.name)}`);
    }
};
  // ✅ FIX: Popular course click → programs tab with ?q= so search persists
  const handleCourseClick = (course) => {
    navigate(`/student/programs?q=${encodeURIComponent(course.query)}`);
  };

  return (
    <div className={`relative min-h-screen overflow-hidden transition-colors duration-500 ${isDarkMode ? 'bg-gradient-to-br from-black via-gray-900 to-black' : 'bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50'}`}>

      {/* Background pattern */}
      <div className={`absolute inset-0 ${isDarkMode ? 'opacity-10' : 'opacity-5'}`}>
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(${isDarkMode ? 'rgba(59,130,246,0.1)' : 'rgba(0,0,0,0.1)'} 1px, transparent 1px), linear-gradient(90deg, ${isDarkMode ? 'rgba(59,130,246,0.1)' : 'rgba(0,0,0,0.1)'} 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />
      </div>
      {isDarkMode && (
        <>
          <div className="absolute top-1/4 left-1/4 w-48 sm:w-96 h-48 sm:h-96 bg-blue-600 rounded-full blur-[120px] opacity-20 animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-48 sm:w-96 h-48 sm:h-96 bg-purple-600 rounded-full blur-[120px] opacity-20 animate-pulse" style={{ animationDelay: '1s' }} />
        </>
      )}

      {/* ── HERO SECTION ── */}
      <section className="relative z-10 px-4 pt-16 sm:pt-20 lg:pt-24 pb-6 sm:pb-10">
        <div className="max-w-7xl mx-auto w-full">

          {/* Live Clock */}
          <div className="flex justify-center lg:justify-start mb-6">
            <div className={`inline-flex items-center gap-4 px-5 py-2 rounded-full border shadow-lg backdrop-blur-md ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center gap-2 text-red-500 font-bold text-[10px] uppercase tracking-widest">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-ping" /> Live Pulse
              </div>
              <div className={`text-xs font-mono font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                {liveTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-8 sm:mb-12 lg:mb-16">

            {/* Left */}
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <div className="text-center lg:text-left mb-6 sm:mb-8">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-3 sm:mb-4 lg:mb-6 leading-tight">
                  <span className={`block drop-shadow-2xl ${isDarkMode ? 'text-white' : 'text-black'}`}>Your Gateway to</span>
                  <span className="block mt-2 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">Global Education</span>
                </h1>
                <p className={`text-sm sm:text-base md:text-lg lg:text-xl mb-4 sm:mb-6 font-light ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Search thousands of universities worldwide and discover the right program for you
                </p>
                <div className="flex items-center justify-center lg:justify-start gap-3 sm:gap-4 lg:gap-6 mb-4 flex-wrap">
                  <div className="flex items-center gap-1 sm:gap-2 text-yellow-500">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 fill-current" />)}
                    <span className={`font-semibold ml-1 text-xs sm:text-sm lg:text-base ${isDarkMode ? 'text-white' : 'text-black'}`}>4.9/5.0</span>
                  </div>
                  <div className={`h-4 w-px ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
                  <div className={`font-semibold text-xs sm:text-sm lg:text-base flex items-center gap-1 sm:gap-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Target className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" /> Trusted by Students
                  </div>
                </div>
              </div>

              {/* ── SEARCH FORM ── */}
              <form onSubmit={handleSearch} className="relative group">
                <div className={`absolute -inset-0.5 sm:-inset-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-xl sm:rounded-2xl blur group-hover:opacity-60 transition duration-1000 ${isDarkMode ? 'opacity-30' : 'opacity-20'}`} />
                <div className={`relative backdrop-blur-2xl rounded-xl sm:rounded-2xl p-3 sm:p-4 border shadow-2xl ${isDarkMode ? 'bg-gray-900/80 border-gray-700' : 'bg-white/90 border-gray-200'}`}>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">

                    {/* Search input with suggestions */}
                    <div className="flex-1 relative group/input" ref={suggestionsRef}>
                      <Search className={`absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 z-10 ${isSearching ? 'text-blue-500 animate-pulse' : 'text-blue-500'} group-focus-within/input:scale-110 transition-transform`} />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={handleSearchInputChange}
                        onFocus={() => searchResults.length > 0 && setShowSuggestions(true)}
                        placeholder="Search courses, universities..."
                        className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-3.5 rounded-lg sm:rounded-xl border-2 focus:outline-none transition-all text-sm sm:text-base font-medium ${isDarkMode ? 'bg-gray-800/50 border-gray-700 text-white placeholder-gray-400 focus:border-blue-500 focus:bg-gray-800' : 'bg-gray-50 border-gray-200 text-black placeholder-gray-500 focus:border-blue-500 focus:bg-white'}`}
                      />

                      {/* ── SUGGESTIONS DROPDOWN ── */}
                      {showSuggestions && searchResults.length > 0 && (
                        <div className={`absolute top-full left-0 right-0 mt-2 rounded-xl shadow-2xl border z-50 overflow-hidden ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                          <div className={`px-3 py-2 border-b text-[10px] font-bold uppercase tracking-wider ${isDarkMode ? 'border-gray-700 text-gray-400 bg-gray-900/50' : 'border-gray-100 text-gray-400 bg-gray-50'}`}>
                            {searchResults.length} result{searchResults.length > 1 ? 's' : ''} — click name to filter list · "Visit" to open directly
                          </div>
                          <div className="max-h-72 overflow-y-auto">
                            {searchResults.slice(0, 8).map(college => (
                              <SuggestionItem
                                key={college._id}
                                college={college}
                                onDirectVisit={handleSuggestionDirectVisit}
                                onSearchList={handleSuggestionSearchList}
                                isDarkMode={isDarkMode}
                              />
                            ))}
                          </div>
                          {searchResults.length > 8 && (
                            <button
                              type="submit"
                              className={`w-full p-2.5 text-xs font-bold text-center border-t transition-colors ${isDarkMode ? 'border-gray-700 text-indigo-400 hover:bg-gray-700' : 'border-gray-100 text-indigo-600 hover:bg-gray-50'}`}
                            >
                              View all {searchResults.length} results →
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Country select */}
                    <select
                      value={selectedCountry}
                      onChange={e => { setSelectedCountry(e.target.value); if (e.target.value !== 'Other') setCustomCountry(''); }}
                      className={`w-full sm:w-44 px-3 sm:px-4 py-3 sm:py-3.5 rounded-lg sm:rounded-xl border-2 focus:outline-none appearance-none cursor-pointer transition-all text-sm sm:text-base font-medium ${isDarkMode ? 'bg-gray-800/50 border-gray-700 text-white focus:border-purple-500 focus:bg-gray-800' : 'bg-gray-50 border-gray-200 text-black focus:border-purple-500 focus:bg-white'}`}
                    >
                      <option value="">All Countries</option>
                      {countries.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>

                    {selectedCountry === 'Other' && (
                      <input
                        type="text" placeholder="Enter country..." value={customCountry}
                        onChange={e => setCustomCountry(e.target.value)}
                        className={`w-full sm:w-44 px-3 sm:px-4 py-3 sm:py-3.5 rounded-lg sm:rounded-xl border-2 focus:outline-none transition-all text-sm sm:text-base font-medium ${isDarkMode ? 'bg-gray-800/50 border-gray-700 text-white placeholder-gray-400 focus:border-green-500' : 'bg-gray-50 border-gray-200 text-black placeholder-gray-500 focus:border-green-500 focus:bg-white'}`}
                      />
                    )}

                    {/* Search button */}
                    <button
                      type="submit"
                      disabled={isSearching}
                      className="group/btn relative bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white px-6 sm:px-8 py-3 sm:py-3.5 rounded-lg sm:rounded-xl font-bold shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2 text-sm sm:text-base overflow-hidden disabled:opacity-50"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-700 via-purple-700 to-pink-700 opacity-0 group-hover/btn:opacity-100 transition-opacity" />
                      {isSearching ? <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 relative z-10 animate-spin" /> : <Search className="w-4 h-4 sm:w-5 sm:h-5 relative z-10" />}
                      <span className="relative z-10">Search</span>
                    </button>
                  </div>
                </div>
              </form>

              {/* Quick Links */}
              <div className="grid grid-cols-2 gap-2 mt-4">
                {(quickLinks.length > 0 ? quickLinks : [
                  { text: 'Admission Deadlines', icon: <Calendar className="w-4 h-4" />, path: '/student/colleges' },
                  { text: 'Scholarships', icon: <DollarSign className="w-4 h-4" />, path: '/student/scholarships' },
                  { text: 'Course Catalog', icon: <BookOpen className="w-4 h-4" />, path: '/student/programs' },
                  { text: 'Psychometric Test', icon: <Clock className="w-4 h-4" />, path: '/services/counselling' }
                ]).map((link, i) => (
                  <motion.button
                    key={i}
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 + 0.5 }}
                    onClick={() => navigate(link.path || '/student/colleges')}
                    className={`flex items-center gap-2 p-2 sm:p-2.5 rounded-lg transition-all hover:scale-105 active:scale-95 text-left ${isDarkMode ? 'bg-gray-800/50 hover:bg-gray-800 text-gray-300 border border-gray-700' : 'bg-gray-100/50 hover:bg-gray-200 text-gray-700 border border-gray-200'}`}
                  >
                    <span className="text-blue-500 flex-shrink-0">{link.icon || <Zap className="w-4 h-4" />}</span>
                    <span className="text-xs sm:text-sm font-semibold truncate">{link.text}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>

            {/* Right: 3D Carousel */}
            <motion.div
              initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}
              className="flex justify-center items-center w-full relative" style={{ minHeight: '400px' }}
            >
              <FloatingIcons />
              {carouselLoading
                ? <div className="absolute inset-0 flex items-center justify-center flex-col text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-purple-500" /><p className="text-gray-400 mt-2">Loading...</p></div>
                : <AnimatedTagline isDarkMode={isDarkMode} items={liveCarouselItems} />}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── FEATURED UNIVERSITIES TICKER ── */}
      <section className="relative z-0 max-w-7xl mx-auto px-4 pt-4 pb-12">
        <h2 className={`text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-center mb-4 ${isDarkMode ? 'text-white' : 'text-black'}`}>
          Explore Top Universities
        </h2>
        {universitiesLoading ? (
          <div className="p-10 text-center">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-blue-500" />
            <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading top universities...</p>
          </div>
        ) : (
          <>
            <UniversitiesTicker isDarkMode={isDarkMode} universities={topUniversities} onCollegeClick={handleTickerCollegeClick} />
            <div className="text-center mt-6">
              <motion.button
                onClick={() => navigate('/student/colleges')}
                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                className="flex items-center justify-center mx-auto px-8 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all gap-2 text-base"
              >
                View All Colleges <ArrowRight className="w-5 h-5" />
              </motion.button>
            </div>
          </>
        )}
      </section>

      {/* ── SERVICES ── */}
      <section className="relative z-0 max-w-7xl mx-auto px-4 pt-12 pb-16">
        <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className={`text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-center mb-12 ${isDarkMode ? 'text-white' : 'text-black'}`}>
          Our Services
        </motion.h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {servicesOffered.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.15 }}
              whileHover={{ y: -12 }}
              onClick={() => navigate(service.target)}
              className="group relative overflow-hidden rounded-3xl p-6 sm:p-7 lg:p-8 cursor-pointer"
              style={{
                backdropFilter: 'blur(24px)',
                background: isDarkMode ? 'linear-gradient(135deg, rgba(17,24,39,0.7), rgba(31,41,55,0.5))' : 'linear-gradient(135deg, rgba(255,255,255,0.9), rgba(249,250,251,0.7))',
                boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.5), inset 0 1px 1px rgba(255,255,255,0.08)' : '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 1px rgba(255,255,255,1)',
                border: isDarkMode ? '1px solid rgba(75,85,99,0.3)' : '1px solid rgba(229,231,235,0.6)'
              }}
            >
              <div className="relative z-10">
                <div className="mb-5">{service.icon}</div>
                <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-black'}`}>{service.title}</h3>
                <ul className={`text-sm space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {service.items.map((item, i) => <li key={i}>• {item}</li>)}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── POPULAR COURSES ── */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 pb-20">
        <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className={`text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-center mb-12 ${isDarkMode ? 'text-white' : 'text-black'}`}>
          Discover Popular Courses
        </motion.h2>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {popularCourses.map((course, index) => (
            <motion.div
              key={course.name}
              initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
              whileHover={{ y: -8, scale: 1.05 }}
              onClick={() => handleCourseClick(course)}
              className={`group flex flex-col items-center text-center p-6 rounded-2xl cursor-pointer transition-all duration-300 ${isDarkMode ? 'bg-gray-800/60 hover:bg-gray-800' : 'bg-white/80 hover:bg-white shadow-lg hover:shadow-xl'}`}
            >
              <div className={`mb-3 p-3 rounded-full group-hover:scale-110 transition-transform ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
                {course.icon}
              </div>
              <h3 className={`text-sm sm:text-base font-bold ${isDarkMode ? 'text-white' : 'text-black'}`}>{course.name}</h3>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Explore programs</p>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/student/programs')}
            className="group relative inline-flex items-center justify-center p-0.5 mb-2 overflow-hidden text-sm font-medium rounded-lg shadow-lg hover:scale-105 active:scale-95 transition-all"
          >
            <span className="absolute inset-0 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500" />
            <span className={`relative px-8 py-3 transition-all ease-in duration-75 rounded-md flex items-center gap-2 font-semibold ${isDarkMode ? 'bg-gray-900 text-white group-hover:bg-opacity-0' : 'bg-white text-gray-900 group-hover:bg-opacity-0 group-hover:text-white'}`}>
              <Globe className="w-5 h-5" /> Explore All Programs
            </span>
          </button>
        </div>
      </section>

    </div>
  );
}